import { SyntheticEvent, MouseEvent, useState, useMemo } from 'react';

import TablePaginationProps from './type';
import Button from '../../Button';
import Icon from '../../Icon';
import Typography from '../../Typography';
import { useWindowSize } from '../../hooks';
import colors from '../../theme/colors/colors';

import {
  DropdownItemContainerStyled,
  DropdownItemStyled,
  DropdownItemWrapperStyled,
  OnRowsPerPageChangeContainerStyled,
  PaginationContainerStyled,
  PaginationEnumerationStyled,
  PaginationEnumerationContainerStyled,
  TablePaginationStyled,
} from './styled';

const TablePagination = ({ ...props }: TablePaginationProps) => {
  const {
    rowsPerPageOptions = [10],
    total = 0,
    rowsPerPage = 10,
    page = 0,
    onPageChange,
    loading = false,
    expandDirection = 'top',
    onRowsPerPageChange,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const totalPages = useMemo(() => Math.ceil(total / rowsPerPage), [total, rowsPerPage]);
  const { isMobile } = useWindowSize({ mobile: 700 });

  const fontSize = '14px';
  const fontColor = colors.gray600;
  const itemHeight = 35;

  const handlePageIncrease = (event: SyntheticEvent<HTMLButtonElement>) =>
    onPageChange(event, page + 1);
  const handlePageDecrease = (event: SyntheticEvent<HTMLButtonElement>) =>
    onPageChange(event, page - 1);

  const getCurrentRowsShown = (): number => {
    if (total === 0) return 0;
    if (page + 1 === totalPages) return total % rowsPerPage || rowsPerPage;
    return rowsPerPage;
  };

  const scrollToSelected = (dropdown: { scrollTop: number }, selectedIndex: number | null) => {
    if (dropdown && selectedIndex !== null) {
      // eslint-disable-next-line no-param-reassign
      dropdown.scrollTop = selectedIndex * itemHeight;
    }
  };

  const renderArrow = (arrowType: 'increase' | 'decrease') => {
    const pageChangeFunction = arrowType === 'increase' ? handlePageIncrease : handlePageDecrease;
    const isActive = arrowType === 'increase' ? page + 1 < totalPages : page > 0;
    const iconName =
      arrowType === 'increase' ? 'keyboardArrowRightRounded' : 'keyboardArrowLeftRounded';
    if (isActive && !loading)
      return (
        <Button
          data-testid={`${arrowType}-button`}
          style={{ padding: '8px' }}
          onClick={pageChangeFunction}
        >
          <Icon size="16px" name={iconName} />
        </Button>
      );
    return <Icon size="16px" name={iconName} />;
  };

  return (
    <TablePaginationStyled {...rest}>
      {rowsPerPageOptions.length > 1 ? (
        <OnRowsPerPageChangeContainerStyled>
          <Typography style={{ fontSize }} color={fontColor}>
            Mostrando {rowsPerPage} resultados
          </Typography>
          <Icon name="arrowDownSpecial" />
        </OnRowsPerPageChangeContainerStyled>
      ) : (
        <Typography style={{ fontSize }} color={fontColor}>
          Mostrando {getCurrentRowsShown()} de {loading && !total ? '?' : total} resultados
        </Typography>
      )}
      <PaginationContainerStyled>
        {!isMobile && renderArrow('decrease')}
        <PaginationEnumerationContainerStyled>
          {totalPages > 1 ? (
            <PaginationEnumerationStyled>
              <Typography style={{ lineHeight: '16px' }}>{total !== 0 ? page + 1 : 0}</Typography>
              <div
                data-testid="dropdown-trigger"
                onClick={() => setIsOpen((oldValue) => !oldValue)}
              >
                {isOpen ? (
                  <div>
                    <Icon style={{ cursor: 'pointer' }} size="16px" name="keyboardArrowUpRounded" />
                    <DropdownItemWrapperStyled
                      expandDirection={expandDirection}
                      open={isOpen}
                      data-testid="page-dropdown"
                    >
                      <DropdownItemContainerStyled
                        ref={(dropdown) => scrollToSelected(dropdown, page || null)}
                      >
                        {[...Array(totalPages).keys()].map((currentPage: number) => {
                          const pageLabel = `${currentPage + 1}`;
                          return (
                            <DropdownItemStyled
                              key={currentPage}
                              name={pageLabel}
                              onClick={(event) =>
                                onPageChange(event as MouseEvent<HTMLButtonElement>, currentPage)
                              }
                              selected={page === currentPage}
                              value={currentPage}
                            >
                              <Typography style={{ lineHeight: '17px' }}>{pageLabel}</Typography>
                            </DropdownItemStyled>
                          );
                        })}
                      </DropdownItemContainerStyled>
                    </DropdownItemWrapperStyled>
                  </div>
                ) : (
                  <Icon style={{ cursor: 'pointer' }} size="16px" name="keyboardArrowDownRounded" />
                )}
              </div>
            </PaginationEnumerationStyled>
          ) : (
            <Typography style={{ lineHeight: '16px' }}>{total !== 0 ? page + 1 : 0}</Typography>
          )}
          <Typography>de {loading && !total ? '?' : totalPages}</Typography>
        </PaginationEnumerationContainerStyled>
        {!isMobile && renderArrow('increase')}
      </PaginationContainerStyled>
    </TablePaginationStyled>
  );
};

export default TablePagination;
